/**
 * Loading placeholders shaped like the content that replaces them.
 *
 * A spinner tells you something is happening; a skeleton tells you *what* is
 * coming, and — because it occupies the same space — the interface does not jump
 * when the data lands. Each function here mirrors one real surface, so a change to
 * that surface's layout should be mirrored here too; a skeleton that no longer
 * matches is worse than none, because it promises the wrong shape.
 *
 * All of them are plain functions rather than components: they hold no state, and
 * a component would only add a lifecycle nobody needs.
 */

/** Repeats a builder `count` times with a key, which every fragment here needs. */
function repeat(count, build) {
  return Array.from({
    length: count
  }, (_, index) => build(index));
}

/**
 * Widths are varied per row so a list does not read as a striped block. Derived
 * from the index rather than random, so the layout is stable across redraws.
 */
function width(index, steps) {
  return steps[index % steps.length] + "%";
}

// ── Message stream ───────────────────────────────────────────────────────────

/**
 * The channel, thread and pinned streams: avatar, author line, message line.
 */
export function MessageStreamSkeleton(rows) {
  if (rows === void 0) {
    rows = 6;
  }
  return m("div", {
    className: "ChatSkeleton",
    "aria-hidden": "true"
  }, repeat(rows, i => m("div", {
    className: "ChatSkeleton-row",
    key: i
  }, m("div", {
    className: "ChatSkeleton-avatar"
  }), m("div", {
    className: "ChatSkeleton-lines"
  }, m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [22, 30, 26])
    }
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [70, 45, 85, 55])
    }
  })))));
}

// ── Full channel pane ────────────────────────────────────────────────────────

/**
 * What the main pane looks like before a channel is ready: the header bar, the
 * stream and the composer, in their final positions.
 *
 * This is the one that matters most — it is the whole right-hand side of the page
 * on first load, and a lone spinner there leaves the layout to snap into place a
 * second later.
 */
export function ChannelSkeleton() {
  return m("div", {
    className: "ChatChannel ChatSkeleton-channel",
    "aria-hidden": "true"
  }, m("div", {
    className: "ChatChannel-header"
  }, m("div", {
    className: "ChatSkeleton-avatar ChatSkeleton-avatar--small"
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: "140px"
    }
  })), m("div", {
    className: "ChatSkeleton-channelStream"
  }, MessageStreamSkeleton(7)), m("div", {
    className: "ChatSkeleton-composer"
  }));
}

// ── Sidebar ──────────────────────────────────────────────────────────────────

/**
 * The channel list: quick links, a section heading, then rows with an icon and a
 * name.
 */
export function SidebarSkeleton() {
  return m("div", {
    className: "ChatSkeleton-sidebar",
    "aria-hidden": "true"
  }, repeat(2, i => m("div", {
    className: "ChatSkeleton-quickLink",
    key: "q" + i
  }, m("div", {
    className: "ChatSkeleton-avatar ChatSkeleton-avatar--tiny"
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [40, 30])
    }
  }))), m("div", {
    className: "ChatSkeleton-heading"
  }), repeat(5, i => m("div", {
    className: "ChatSkeleton-channelRow",
    key: "c" + i
  }, m("div", {
    className: "ChatSkeleton-avatar ChatSkeleton-avatar--tiny"
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [55, 40, 65, 35, 50])
    }
  }))));
}

// ── Browse channels ──────────────────────────────────────────────────────────

/** Cards with the icon, title, description, meta row and action button. */
export function BrowseSkeleton(cards) {
  if (cards === void 0) {
    cards = 4;
  }
  return m("div", {
    className: "ChatBrowse-list",
    "aria-hidden": "true"
  }, repeat(cards, i => m("div", {
    className: "ChatBrowseCard ChatSkeleton",
    key: i
  }, m("div", {
    className: "ChatSkeleton-avatar ChatSkeleton-avatar--large"
  }), m("div", {
    className: "ChatSkeleton-lines"
  }, m("div", {
    className: "ChatSkeleton-line ChatSkeleton-line--title",
    style: {
      width: width(i, [30, 22, 38, 26])
    }
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [70, 55, 80, 45])
    }
  }), m("div", {
    className: "ChatSkeleton-line ChatSkeleton-line--meta",
    style: {
      width: "35%"
    }
  })), m("div", {
    className: "ChatSkeleton-button"
  }))));
}

// ── Search results ───────────────────────────────────────────────────────────

export function SearchResultsSkeleton(rows) {
  if (rows === void 0) {
    rows = 5;
  }
  return m("div", {
    className: "ChatSkeleton ChatSkeleton--flush",
    "aria-hidden": "true"
  }, repeat(rows, i => m("div", {
    className: "ChatSkeleton-row ChatSkeleton-row--tight",
    key: i
  }, m("div", {
    className: "ChatSkeleton-avatar ChatSkeleton-avatar--small"
  }), m("div", {
    className: "ChatSkeleton-lines"
  }, m("div", {
    className: "ChatSkeleton-line ChatSkeleton-line--meta",
    style: {
      width: width(i, [40, 32, 45])
    }
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [75, 60, 85, 50])
    }
  })))));
}

// ── My threads ───────────────────────────────────────────────────────────────

export function ThreadsSkeleton(rows) {
  if (rows === void 0) {
    rows = 5;
  }
  return m("div", {
    className: "ChatSkeleton ChatSkeleton--flush",
    "aria-hidden": "true"
  }, repeat(rows, i => m("div", {
    className: "ChatSkeleton-row ChatSkeleton-row--tight",
    key: i
  }, m("div", {
    className: "ChatSkeleton-avatar ChatSkeleton-avatar--tiny"
  }), m("div", {
    className: "ChatSkeleton-lines"
  }, m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [50, 65, 40, 58])
    }
  }), m("div", {
    className: "ChatSkeleton-line ChatSkeleton-line--meta",
    style: {
      width: "30%"
    }
  })))));
}

// ── Member list ──────────────────────────────────────────────────────────────

export function MembersSkeleton(rows) {
  if (rows === void 0) {
    rows = 6;
  }
  return m("div", {
    className: "ChatSkeleton ChatSkeleton--flush",
    "aria-hidden": "true"
  }, repeat(rows, i => m("div", {
    className: "ChatSkeleton-memberRow",
    key: i
  }, m("div", {
    className: "ChatSkeleton-avatar ChatSkeleton-avatar--small"
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [45, 32, 55, 38])
    }
  }))));
}

// ── Edit history ─────────────────────────────────────────────────────────────

export function RevisionsSkeleton(rows) {
  if (rows === void 0) {
    rows = 3;
  }
  return m("div", {
    className: "ChatRevisions-list",
    "aria-hidden": "true"
  }, repeat(rows, i => m("div", {
    className: "ChatRevisions-entry ChatSkeleton",
    key: i
  }, m("div", {
    className: "ChatSkeleton-line ChatSkeleton-line--meta",
    style: {
      width: width(i, [35, 28, 42])
    }
  }), m("div", {
    className: "ChatSkeleton-line",
    style: {
      width: width(i, [80, 60, 90])
    }
  }))));
}
flarum.reg.add('ramon-chat', 'forum/components/Skeletons', { MessageStreamSkeleton: MessageStreamSkeleton,ChannelSkeleton: ChannelSkeleton,SidebarSkeleton: SidebarSkeleton,BrowseSkeleton: BrowseSkeleton,SearchResultsSkeleton: SearchResultsSkeleton,ThreadsSkeleton: ThreadsSkeleton,MembersSkeleton: MembersSkeleton,RevisionsSkeleton: RevisionsSkeleton, });